'use client';

import { useEffect, useState } from 'react';
import type { StickyCTABarResolved } from './types';
import { Icon } from '@/components/Icon';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useShellState } from '@/lib/useShellState';

interface StickyCTABarProps {
  /**
   * The bar the `(main)` layout resolved for the pathname it server-rendered,
   * or `null` when that page shows none. Only the *first* page's answer: the
   * layout does not re-run on a client-side navigation, so from there on the
   * bar follows `useShellState` — which also decides whether it exists at all,
   * because `showStickyCTABar` is a property of the Page.
   */
  initial: StickyCTABarResolved | null;
}

const REVEAL_THRESHOLD = 200;

export const StickyCTABar = ({ initial }: StickyCTABarProps) => {
  const resolved = useShellState(initial, (state) => state.stickyCtaBar);
  const [visible, setVisible] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  // Scroll tracking — preserved verbatim from the original DurationTeaser
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > REVEAL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Input focus tracking
  useEffect(() => {
    const isEditable = (el: Element | null) => {
      if (!(el instanceof HTMLElement)) {
        return false;
      }
      const tag = el.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el.isContentEditable
      );
    };
    const sync = () => setInputFocused(isEditable(document.activeElement));
    sync();
    document.addEventListener('focusin', sync, true);
    document.addEventListener('focusout', sync, true);
    return () => {
      document.removeEventListener('focusin', sync, true);
      document.removeEventListener('focusout', sync, true);
    };
  }, []);

  const shown = visible && !inputFocused;

  // After the hooks, never before: a page without a bar still runs them, so
  // the scroll and focus listeners stay mounted across a navigation onto a
  // page that does have one.
  if (!resolved) {
    return null;
  }

  const telHref = `tel:${resolved.phone.replace(/[\s/]/g, '')}`;
  const displayPhone = resolved.matelsoPhoneLabel || resolved.phone;

  const trackPhoneClick = () =>
    pushCmsEventTracking(resolved.leftButtonTracking, {
      label: resolved.callLabel,
      phone: displayPhone,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    });

  const trackCtaClick = (label?: string) => () =>
    pushCmsEventTracking(resolved.rightButtonTracking, {
      label,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    });

  return (
    <div
      aria-hidden={!shown}
      inert={!shown}
      className={[
        'sticky-cta-bar',
        'rounded-t-2xl md:shadow-[0_-2px_8px_rgba(0,0,0,0.1)]',
        'lg:border-card-border fixed right-0 bottom-0 left-0 z-50 py-3 transition-transform duration-300 lg:border-t lg:bg-white',
        shown ? 'translate-y-0' : 'translate-y-full',
      ].join(' ')}
    >
      {/* Mobile: combined pill, no headings */}
      <div className="mx-auto w-full px-2 py-1 lg:hidden">
        <div className="border-primary grid grid-cols-2 overflow-hidden rounded-3xl border text-xs">
          <a
            href={telHref}
            onClick={trackPhoneClick}
            className="text-primary hover:bg-primary/5 flex flex-col items-center bg-white py-3 transition-colors"
          >
            <span className="font-medium">{resolved.callLabel}</span>
            {/* A phone number never wraps, even when it contains spaces */}
            <span className="flex items-center gap-2 font-bold whitespace-nowrap">
              <Icon src="/icons/phone.svg" size={16} />
              {displayPhone}
            </span>
          </a>
          <a
            href={resolved.ctaHref}
            onClick={trackCtaClick(resolved.ctaLabelMobile)}
            className="bg-primary hover:bg-primary/90 flex items-center justify-center px-8 py-3 font-medium text-white transition-colors"
          >
            {resolved.ctaLabelMobile}
          </a>
        </div>
      </div>

      {/* Desktop: two columns with headings */}
      <div className="mx-auto hidden max-w-6xl grid-cols-2 gap-12 p-2 lg:grid lg:px-9">
        <div className="flex flex-col items-start gap-4">
          <div className="mx-4 text-lg">{resolved.leftTitle}</div>
          <a
            href={telHref}
            onClick={trackPhoneClick}
            className="rounded-button border-primary hover:bg-card-bg inline-flex w-full items-center justify-between gap-4 border px-6 py-3 text-lg font-medium transition-colors"
          >
            <span className="text-primary font-medium">
              {resolved.callLabel}
            </span>
            <span className="text-primary flex shrink-0 items-center gap-1.5 font-bold whitespace-nowrap">
              <Icon src="/icons/phone.svg" size={16} />
              {displayPhone}
            </span>
          </a>
        </div>
        <div className="flex flex-col items-start gap-4">
          <div className="mx-4 text-lg">{resolved.rightTitle}</div>
          <a
            href={resolved.ctaHref}
            onClick={trackCtaClick(resolved.ctaLabel)}
            className="rounded-button bg-primary hover:bg-primary/90 inline-flex w-full items-center justify-center px-12 py-3 text-lg font-medium text-white transition-colors"
          >
            {resolved.ctaLabel}
          </a>
        </div>
      </div>
    </div>
  );
};
